import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, tap} from "rxjs";
import {NycAuth, UserOverview} from "../model/user.model";
import {environment} from "../environments/environment";
import {
  CheckEligibilityCommand,
  ClearIncomeNycIdCommand,
  ConnectToNycApplicationCommand,
  LinkAuthToUserCommand,
  MarkAsPaidCommand,
  SendCustomNotificationCommand,
  SendNotificationCommand,
  ResetSyncStatusCommand,
  UpdateNycAuthCommand,
  UserDto
} from "admin-api-dto";

@Injectable({
  providedIn: 'root'
})
export class UserService {
  get users$(): Observable<UserOverview[]> {
    return this._users$.asObservable();
  }

  private readonly _users$ = new BehaviorSubject<UserOverview[]>([]);

  constructor(private readonly http: HttpClient) {
  }


  deleteUser(id: string) {
    return this.http.delete(`${environment.apiUrl}/api/private/users/${id}`).pipe();
  }

  clearMemberIncomeNycId(id: string, command: ClearIncomeNycIdCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/clear-income-nyc-id`, command).pipe(
    );
  }

  redistributeIncome(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/redistribute-income`, {}).pipe(
    );
  }

  linkAuthToOtherUser(id: string, command: LinkAuthToUserCommand) {
    return this.http.put<{
      updatedMappings: number;
      sourceUserId: string;
      targetUserId: string;
      deleted: boolean;
    }>(`${environment.apiUrl}/api/private/users/${id}/link-auth`, command);
  }

  loginWith(id: string) {
    return this.http.post<{ url: string }>(`${environment.apiUrl}/api/private/users/${id}/login-with`, {}).pipe(
      tap((data) => {
        window.open(
          data.url
        )
      })
    )
  }

  markAsRegisteredInNyc(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-as-registered-in-nyc`, {}).pipe(
    );
  }

  updateNycProfileAuth(id: string, command: UpdateNycAuthCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/update-nyc-profile-auth`, command).pipe(
    );
  }

  getUser(id: string): Observable<UserDto> {
    return this.http.get<UserDto>(`${environment.apiUrl}/api/private/users/${id}`);
  }

  requestCsvExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/users/csv`);
  }

  getCsvDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/users/csv/signed`, {
      params: {s3Key}
    });
  }

  requestCancelEventsExport() {
    return this.http.get<{ s3Key: string }>(`${environment.apiUrl}/api/private/cancel-data/events-csv`);
  }

  getCancelEventsDownloadUrl(s3Key: string) {
    return this.http.get<{ url: string }>(`${environment.apiUrl}/api/private/cancel-data/events-csv/signed`, {
      params: {s3Key}
    });
  }

  getNycAuth(id: string): Observable<NycAuth> {
    return this.http.get<NycAuth>(`${environment.apiUrl}/api/private/users/${id}/nyc-auth`);
  }

  getAllNycAuth(): Observable<NycAuth[]> {
    return this.http.get<NycAuth[]>(`${environment.apiUrl}/api/private/users/nyc-auth`);
  }

  getActiveUsersNotRegistered() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        'not-registered': 'true'
      }
    });
  }

  getByEmail(email: string) {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        email
      }
    });
  }

  getByStatus(statusOption: "signup-flow" | "mandatory-flow" | "active") {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        status: statusOption
      }
    });
  }

  getActiveNotSyncedUsers() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        'not-synced': 'true'
      }
    });
  }

  getUsersAfterSignupFlow() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`, {
      params: {
        'after-signup-flow': 'true'
      }
    });
  }

  resetLoginSyncResult(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reset-login-sync-result`, {});
  }

  markCurrentProfileComplete(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-current-profile-complete`, {});
  }

  reconnectHousehold(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reconnect-household`, {});
  }

  markResetPassword(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-reset-password`, {});
  }

  getActiveUsers() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users`);
  }

  getFailedWithChildren() {
    return this.http.get<UserOverview[]>(`${environment.apiUrl}/api/private/users/get-synced-with-children`, {});
  }

  getById(userId: string) {
    return this.http.get<UserOverview>(`${environment.apiUrl}/api/private/users/${userId}`, {});
  }

  unlockWaitingForEmailTwo(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/unlock-waiting`, {});
  }

  resetFailedApplications(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reset-failed-applications`, {});
  }

  toggleIgnore(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/toggle-ignore`, {});
  }

  triggerScheduledResync(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/resync-applications`, {});
  }

  checkEligibility(id: string, command: CheckEligibilityCommand) {
    return this.http.put<{
      result: string
    }>(`${environment.apiUrl}/ap$i/private/users/${id}/check-eligibility`, command);
  }

  connectToNycApplication(id: string, command: ConnectToNycApplicationCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/connect-nyc-application`, command);
  }

  addMissingNycIds(id: string) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/add-missing-nyc-ids`, {});
  }

  resetSyncStatus(id: string, command: ResetSyncStatusCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/reset-sync-status`, command)
  }

  markAsPaid(id: string, param: MarkAsPaidCommand) {
    return this.http.put(`${environment.apiUrl}/api/private/users/${id}/mark-as-paid`, param)
  }

  sendCustomNotification(id: string, command: SendCustomNotificationCommand) {
    return this.http.post<{notificationId: string}>(`${environment.apiUrl}/api/private/users/${id}/custom-notifications`, command);
  }

  sendNotification(id: string, command: SendNotificationCommand) {
    return this.http.post<{notificationId: string}>(`${environment.apiUrl}/api/private/users/${id}/notifications`, command);
  }
}
